import { faBug } from "@fortawesome/duotone-regular-svg-icons/faBug";
import { faCopyright } from "@fortawesome/duotone-regular-svg-icons/faCopyright";
import { faFileContract } from "@fortawesome/duotone-regular-svg-icons/faFileContract";
import { faGear } from "@fortawesome/duotone-regular-svg-icons/faGear";
import { faLeaf } from "@fortawesome/duotone-regular-svg-icons/faLeaf";
import { faMegaphone } from "@fortawesome/duotone-regular-svg-icons/faMegaphone";
import { faSignOut } from "@fortawesome/duotone-regular-svg-icons/faSignOut";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Center, Menu } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { getAuth } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { useUser } from "reactfire";
import { searchByDriveTime } from "../store/slices/campgroundSearch";
import { useAppDispatch } from "../store/store";
import { isDevMode } from "../utils/debug";
import { openAttributionsModal } from "./AttributionsModal";
import { openLeaveNoTraceModal } from "./LeaveNoTraceModal";
import { openPoliciesModal } from "./PoliciesModal";
import { sentryIntegrationsReady } from "../utils/sentry-deferred";
import ThemeSwitch from "./ThemeSwitch";
import { NavbarLink } from "./navbar/Navbar";

const AppMenu = () => {
  const [feedback, setFeedback] =
    useState<ReturnType<typeof Sentry.getFeedback>>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // The feedback integration is attached after startup (see main.tsx), so it
    // is not available at mount - reading it once here always returned
    // undefined and left the feedback button inert.
    let active = true;
    void sentryIntegrationsReady.then(() => {
      if (active) setFeedback(Sentry.getFeedback());
    });
    return () => {
      active = false;
    };
  }, []);

  const user = useUser();
  const auth = getAuth();

  // Attach the Sentry feedback form to a button once it's available
  const attachFeedbackForm = useCallback(
    (buttonRef: HTMLButtonElement, extraOptions?: object) => {
      if (feedback && buttonRef) {
        const unsubscribe = feedback.attachTo(buttonRef, extraOptions);
        return () => {
          unsubscribe();
        };
      }
      return () => {};
    },
    [feedback]
  );

  return (
    <Menu shadow="md" width={250} zIndex={401} withinPortal position="right">
      <Menu.Target>
        <NavbarLink icon={faGear} label="Settings" />
      </Menu.Target>

      <Menu.Dropdown>
        {user.data && (
          <>
            <Menu.Label>Account</Menu.Label>
            <Menu.Item disabled>{user.data.email}</Menu.Item>
            <Menu.Item
              leftSection={<FontAwesomeIcon icon={faSignOut} />}
              onClick={() => {
                auth.signOut();
              }}
            >
              Logout
            </Menu.Item>
          </>
        )}

        <Menu.Label>Theme</Menu.Label>
        <Menu.Item
          component={() => {
            return (
              <Center w="100%" pl="sm" pr="sm">
                <ThemeSwitch style={{ flex: 1 }} />
              </Center>
            );
          }}
        />

        {feedback && (
          <>
            <Menu.Label>Contact Us</Menu.Label>
            <Menu.Item
              leftSection={<FontAwesomeIcon icon={faMegaphone} />}
              ref={(el) =>
                attachFeedbackForm(el!, {
                  formTitle: "Send Feedback",
                  messagePlaceholder: "Tell us anything!",
                  submitButtonLabel: "Send Feedback",
                  successMessageText: "Thank you for your feedback!",
                })
              }
            >
              Send Feedback
            </Menu.Item>
            <Menu.Item
              leftSection={<FontAwesomeIcon icon={faBug} />}
              ref={(el) => attachFeedbackForm(el!)}
            >
              Report a Bug
            </Menu.Item>
          </>
        )}

        <Menu.Label>About</Menu.Label>
        <Menu.Item
          leftSection={<FontAwesomeIcon icon={faFileContract} />}
          onClick={openPoliciesModal}
        >
          Policies
        </Menu.Item>
        <Menu.Item
          leftSection={<FontAwesomeIcon icon={faCopyright} />}
          onClick={openAttributionsModal}
        >
          Attributions
        </Menu.Item>
        <Menu.Item
          leftSection={<FontAwesomeIcon icon={faLeaf} />}
          onClick={() => openLeaveNoTraceModal()}
        >
          Stewardship
        </Menu.Item>
        <Menu.Item disabled>
          v{APP_VERSION} ({import.meta.env.VITE_APP_VERSION?.substring(0, 7)})
        </Menu.Item>

        {isDevMode() && (
          <>
            <Menu.Divider />
            <Menu.Item
              onClick={() =>
                dispatch(
                  searchByDriveTime({
                    latDeg: 32.72793,
                    lonDeg: -117.15529,
                    driveTimeMins: 60,
                  })
                )
              }
            >
              San Diego
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default AppMenu;
